/**
 * Filter catalog — THE single source of truth for buyer-facing search filters.
 *
 * One entry per SearchFilters key. Everything else derives from this table:
 *  1. `/api/search`'s zod FilterSchema (search/index.ts builds it from the
 *     `kind` column) — a new filter is validated the moment it's added here.
 *  2. Gemma's `search_stones` tool schema (chat/tools.ts builds the Gemini
 *     function declaration from the `llm` column) — the AI agent learns a new
 *     filter automatically; no prompt edit needed. `llm: null` hides a key
 *     from the agent (internal/leak-prone knobs).
 *  3. The `/search` URL param mapping, BOTH directions and BOTH packages —
 *     web/src/lib/filterHelpers.ts (chips → URL) and the chat "Browse all
 *     matches" deep-link serialize through browseParamsFromFilters below, so
 *     a chat search and a sidebar search can never drift apart.
 *
 * Adding a filter = add the applyFilters predicate (search/filter.ts) + ONE
 * row here. The catalog parity test (filterCatalog.test.ts) fails the build
 * if FilterSchema and this table diverge.
 *
 * Browser-safe like shapes.ts: dependency-free (no zod, no Node imports) —
 * the web bundle imports this file directly.
 */
import { SHAPES_CORE, SHAPES_EXTENDED } from './shapes'

export type FilterKind =
  | 'strings'   // string[]  — csv in the URL
  | 'numbers'   // number[]  — csv in the URL
  | 'number'    // single number
  | 'flag'      // boolean   — '1' in the URL when true
  | 'string'    // single string
  | 'sort'      // sortBy enum

/**
 * `buzz_desc` (global engagement score) landed on /api/search's sort enum
 * while this catalog sat on a branch. It is added HERE rather than special-
 * cased at either consumer, because that is the whole contract: the catalog is
 * the SSOT, the parity test compares it against the live `SearchFilters` type,
 * and the fix for drift is always to teach the catalog — never to loosen the
 * test. Listing it also hands Gemma the sort for free ("what's popular").
 */
export const SORT_VALUES = ['carat_asc', 'carat_desc', 'price_asc', 'price_desc', 'ppc_asc', 'ppc_desc', 'buzz_desc'] as const
export type SortValue = typeof SORT_VALUES[number]

export interface FilterDef {
  /** SearchFilters key (shared/types.ts + web/src/api/types.ts). */
  key: string
  kind: FilterKind
  /** /search URL param name. null = never serialized into a browse link. */
  url: string | null
  /**
   * Gemini tool-argument description for the agent. Written for an LLM:
   * closed vocabularies spelled out, colloquial mappings included.
   * null = the agent cannot set this filter (internal / leak-prone).
   */
  llm: string | null
}

const GRADE_LADDER = 'Ideal, Excellent, Very Good, Good, Fair'
const DORMANT_FLAG_CAVEAT =
  'Strict opt-in: only stones explicitly flagged pass, and most inventory carries no flag — warn the user results may be sparse.'

export const FILTER_CATALOG: FilterDef[] = [
  {
    key: 'shape', kind: 'strings', url: 'shape',
    llm: `Diamond shapes. Canonical labels ONLY: ${[...SHAPES_CORE, ...SHAPES_EXTENDED].join(', ')}. ` +
      'Map colloquial phrasing to canonical ("round brilliant"→Round, "square emerald"→Asscher, "elongated cushion"→Cushion + ratioMin 1.3, "cut-cornered rectangular"→Radiant).',
  },
  {
    key: 'color', kind: 'strings', url: 'color',
    llm: 'White color grades, single letters D through Z (D best). "D-F" / "colorless" → ["D","E","F"]; "near colorless" → ["G","H","I","J"]. For fancy colors use fancyHues/fancyIntensities instead.',
  },
  {
    // Superseded by fancyHues for the agent — kept for URL/API back-compat.
    key: 'colorLike', kind: 'string', url: 'colorLike', llm: null,
  },
  {
    key: 'fancyHues', kind: 'strings', url: 'fancy',
    llm: 'Fancy colour hues (substring OR-match): Yellow, Pink, Blue, Green, Orange, Brown, Purple, Red, Gray, Violet. "canary"→Yellow.',
  },
  {
    key: 'fancyIntensities', kind: 'strings', url: 'intensity',
    llm: 'Fancy intensity grades: Fancy Light, Fancy, Fancy Intense, Fancy Vivid, Fancy Deep, Fancy Dark. Bare "Fancy" matches only the unqualified grade.',
  },
  {
    key: 'clarity', kind: 'strings', url: 'clarity',
    llm: 'Clarity grades: FL, IF, VVS1, VVS2, VS1, VS2, SI1, SI2, SI3, I1, I2, I3 (FL best). "VS1 or better" → ["FL","IF","VVS1","VVS2","VS1"]; "eye clean-ish SI" → ["SI1","SI2"].',
  },
  {
    key: 'cut', kind: 'strings', url: 'cut',
    llm: `Cut grades: ${GRADE_LADDER}. "3EX"/"triple excellent" → cut+polish+symmetry all ["Excellent"] (Ideal outranks Excellent — "EX or better" → ["Ideal","Excellent"]).`,
  },
  { key: 'polish', kind: 'strings', url: 'polish', llm: `Polish grades: ${GRADE_LADDER}.` },
  { key: 'symmetry', kind: 'strings', url: 'symmetry', llm: `Symmetry grades: ${GRADE_LADDER}.` },
  {
    key: 'fluorescence', kind: 'strings', url: 'fluorescence',
    llm: 'Fluorescence: None, Faint, Slight, Medium, Strong, Very Strong. "no fluoro" → ["None"].',
  },
  {
    key: 'lab', kind: 'strings', url: 'lab',
    llm: 'Certifying lab: IGI, GIA, GCAL, HRD, SGL. "None" = uncertified stones.',
  },
  {
    key: 'stoneType', kind: 'strings', url: 'growth',
    llm: 'Growth type: CVD, HPHT, Natural. The catalog is predominantly lab-grown (CVD/HPHT).',
  },
  { key: 'heartsArrows', kind: 'flag', url: 'ha', llm: `true → Hearts & Arrows stones only. ${DORMANT_FLAG_CAVEAT}` },
  { key: 'eyeClean', kind: 'flag', url: 'eyeClean', llm: `true → eye-clean-flagged stones only. ${DORMANT_FLAG_CAVEAT}` },
  { key: 'noBGM', kind: 'flag', url: 'noBGM', llm: `true → certified free of brown/green/milky tint. ${DORMANT_FLAG_CAVEAT}` },
  { key: 'caratMin', kind: 'number', url: 'caratMin', llm: 'Minimum carat weight. "3 ct +" / "above 3 carats" → 3.' },
  { key: 'caratMax', kind: 'number', url: 'caratMax', llm: 'Maximum carat weight. "under 2ct" → 2.' },
  { key: 'priceMin', kind: 'number', url: 'priceMin', llm: 'Minimum TOTAL price in USD.' },
  { key: 'priceMax', kind: 'number', url: 'priceMax', llm: 'Maximum TOTAL price in USD ("budget $5k" → 5000).' },
  { key: 'ppcMin', kind: 'number', url: 'ppcMin', llm: 'Minimum price per carat ($/ct) — the axis the trade compares on.' },
  { key: 'ppcMax', kind: 'number', url: 'ppcMax', llm: 'Maximum price per carat ($/ct).' },
  { key: 'lengthMin', kind: 'number', url: 'lengthMin', llm: 'Minimum length in mm (longest axis).' },
  { key: 'lengthMax', kind: 'number', url: 'lengthMax', llm: 'Maximum length in mm.' },
  { key: 'widthMin', kind: 'number', url: 'widthMin', llm: 'Minimum width in mm.' },
  { key: 'widthMax', kind: 'number', url: 'widthMax', llm: 'Maximum width in mm.' },
  { key: 'ratioMin', kind: 'number', url: 'ratioMin', llm: 'Minimum length/width ratio. Elongated shapes ("elongated cushion") → 1.3+.' },
  { key: 'ratioMax', kind: 'number', url: 'ratioMax', llm: 'Maximum length/width ratio. "square-ish" → 1.1.' },
  {
    key: 'stockIds', kind: 'strings', url: 'stockIds',
    llm: 'Exact stock IDs the user names (e.g. "AJ5138", "VL-158201", with or without the leading #). Use for lookups ("show me AJ5138") and comparisons (pass several).',
  },
  // Supplier scoping is UI-only (KYC-gated facet) — the agent never sets it,
  // so a prompt-injected "show me supplier 104's stones" can't smuggle a
  // supplier-targeted probe through chat.
  { key: 'supplierId', kind: 'number', url: 'supplier', llm: null },
  { key: 'supplierIds', kind: 'numbers', url: 'suppliers', llm: null },
  { key: 'supplierExclude', kind: 'flag', url: 'suppliersEx', llm: null },
  {
    key: 'country', kind: 'strings', url: 'country',
    llm: 'Stone origin country, e.g. "United States", "India".',
  },
  { key: 'countryExclude', kind: 'flag', url: 'countryEx', llm: 'true → the country list is an EXCLUSION ("not from India").' },
  {
    key: 'sortBy', kind: 'sort', url: 'sort',
    llm: `One of ${SORT_VALUES.join(', ')}. "biggest first"→carat_desc, "cheapest"→price_asc, "best value"→ppc_asc, ` +
      '"popular"/"trending"/"what are people looking at"→buzz_desc. ' +
      'Omit when the user gave only a lower/upper bound — the platform then orders results closest to that bound automatically.',
  },
]

/** Fast lookup by SearchFilters key. */
export const FILTER_BY_KEY: Record<string, FilterDef> = Object.fromEntries(
  FILTER_CATALOG.map((d) => [d.key, d]),
)

// ── URL serialization (shared by web deep-links + chat browse-all links) ────

/** Any filter-shaped object (SearchFilters or a partial of it). */
type Filterish = object

/**
 * Serialize filters → ordered [param, value] pairs for /search. Only
 * catalog-listed, URL-mapped, populated keys emit — unknown keys are dropped,
 * so a server-side internal knob can never leak into a shareable link.
 */
export function browseParamsFromFilters(f: Filterish): Array<[string, string]> {
  const out: Array<[string, string]> = []
  for (const d of FILTER_CATALOG) {
    if (!d.url) continue
    const v = (f as Record<string, unknown>)[d.key]
    if (v == null) continue
    switch (d.kind) {
      case 'strings':
        if (Array.isArray(v) && v.length) out.push([d.url, v.join(',')])
        break
      case 'numbers':
        if (Array.isArray(v) && v.length) out.push([d.url, v.join(',')])
        break
      case 'number':
        if (typeof v === 'number' && Number.isFinite(v)) out.push([d.url, String(v)])
        break
      case 'flag':
        if (v === true) out.push([d.url, '1'])
        break
      case 'string':
        if (typeof v === 'string' && v) out.push([d.url, v])
        break
      case 'sort':
        if (typeof v === 'string' && (SORT_VALUES as readonly string[]).includes(v)) out.push([d.url, v])
        break
    }
  }
  return out
}

/** Convenience: full "?a=b&c=d" query string (empty string when no filters). */
export function browseQueryString(f: Filterish): string {
  const pairs = browseParamsFromFilters(f)
  if (!pairs.length) return ''
  const p = new URLSearchParams()
  for (const [k, v] of pairs) p.set(k, v)
  return '?' + p.toString()
}

/**
 * Parse /search URL params → filters. `get` abstracts URLSearchParams so both
 * packages share the exact inverse of browseParamsFromFilters.
 */
export function filtersFromBrowseParams(get: (param: string) => string | null): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const d of FILTER_CATALOG) {
    if (!d.url) continue
    const raw = get(d.url)
    if (raw == null || raw === '') continue
    switch (d.kind) {
      case 'strings': {
        const list = raw.split(',').filter(Boolean)
        if (list.length) out[d.key] = list
        break
      }
      case 'numbers': {
        const list = raw.split(',').map(Number).filter((n) => Number.isFinite(n))
        if (list.length) out[d.key] = list
        break
      }
      case 'number': {
        const n = Number(raw)
        if (Number.isFinite(n)) out[d.key] = n
        break
      }
      case 'flag':
        if (raw === '1') out[d.key] = true
        break
      case 'string':
        out[d.key] = raw
        break
      case 'sort':
        if ((SORT_VALUES as readonly string[]).includes(raw)) out[d.key] = raw
        break
    }
  }
  return out
}
